﻿// © Microsoft Corporation. All rights reserved.

import { IconHandledProps, IconProps, IconUnhandledProps } from "./Icon.props";
import React, { CSSProperties } from "react";

import Foundation from "@microsoft/fast-components-foundation-react";
import { Glyph } from ".";
import { HandledPropsKeys } from "@msnews/core";

/**
 * Icon state
 */
interface IconState {
    /**
     * Svg component that we are lazy loading. Normally, we would use React.lazy and React.suspense, but those are not recommended for 
     * production with Preact.  Using them will cause CardActions to break.
     */
    svgComponent?: React.ComponentType<React.SVGAttributes<SVGSVGElement>>;
}

/**
 * Icon component
 * @class - Icon
 * @classdesc - The Icon component. Renders the image with respective glyph icon
 * @default
 */
export class Icon extends Foundation<IconHandledProps, IconUnhandledProps, IconState> {
    /**
     * Component HandledProps enumeration. Anything not enumerated here will be included in the experience root HTML tag via unhandledProps()
     */
    protected handledProps: HandledPropsKeys<IconHandledProps> = {
        ariaLabel: undefined,
        fill: undefined,
        glyphType: undefined,
        height: undefined,
        managedClasses: undefined,
        onError: undefined,
        width: undefined,
        wrapperClassName: undefined
    };

    constructor(props: IconProps) {
        super(props);
        this.state = {
            svgComponent: null
        };
    }

    /**
     * If glyphType is a promise, lazy load the component with the promise. Once loaded, update state with the new
     * component.
     * @method
     */
    public componentDidMount() {
        const { glyphType } = this.props;

        if (typeof glyphType === "function") {
            glyphType().then((m) => {
                this.setState({ svgComponent: m.ReactComponent });
            });
        }
    }

    /**
     * If glyphType is a promise and has changed, lazy load the new component with the promise. Once loaded, update
     * state with the new component.
     * @method
     * @param {IconProps} prevProps - props before update
     */
    public componentDidUpdate(prevProps: IconProps) {
        const { glyphType } = this.props;

        if (typeof glyphType === "function" && glyphType !== prevProps.glyphType) {
            glyphType().then((m) => {
                this.setState({ svgComponent: m.ReactComponent });
            });
        }
    }

    /**
     * Updating state before render. If there is no glyphType, we can set this.state.svgComponent to null. This avoids
     * an extra render if we tried to use setState in componentDidUpdate to set this.state.svgComponent to null.
     * @method
     * @param {IconProps} props - props to be used in the upcoming render
     * @param {IconState} state - state to be used in the upcoming render
     * @returns {IconState} - updated state to be used in the next render
     */
    public static getDerivedStateFromProps(props: IconProps, state: IconState): IconState {
        const { glyphType } = props;

        if (!glyphType) {
            return {
                svgComponent: null
            }
        } else {
            return state;
        }
    }

    /**
     * Render our lazy loaded svg component
     * @method
     * @param {React.ComponentType} LazyLoadedSvgComponent - the lazy loaded svg component we're going to render
     * @returns {JSX.Element} A JSX element with a span wrapping our lazy loaded svg component
     */
    private renderSvgComponent(LazyLoadedSvgComponent: React.ComponentType): JSX.Element {
        const { height, width, fill, managedClasses } = this.props;
        const attrProps: React.SVGAttributes<SVGSVGElement> = {};

        if (width) {
            attrProps.width = width;
        }

        if (height) {
            attrProps.height = height;
        }

        return (<LazyLoadedSvgComponent
            fill={fill}
            className={this.generateClassNames(managedClasses.icon_inline)}
            {...attrProps} />);
    }

    /**
     * Component renderer
     * @public
     * @method
     * @returns {JSX.Element} the image component containing a glyph icon
     */
    public render(): JSX.Element {
        const { ariaLabel, height, managedClasses, glyphType, width, wrapperClassName, fill } = this.props;
        if (!glyphType) {
            return (null);
        }
        const role = ariaLabel ? "img" : "presentation";

        if (typeof glyphType === "string") {
            // when glyphType is string, use img tag
            let icon = glyphType as string;
            if (Glyph[icon]) {
                icon = Glyph[icon];
            }
            return (<img src={icon}
                style={{ width, height }}
                className={this.generateClassNames(managedClasses.icon)}
                title={ariaLabel || null}
                aria-label={ariaLabel || null}
                role = {role}
                aria-hidden={!ariaLabel} />);
        }

        let wrapperCss: CSSProperties = {};
        if (width) {
            wrapperCss.width = width;
        }
        if (height) {
            wrapperCss.height = height;
        }

        if (!wrapperCss.width && !wrapperCss.height) {
            wrapperCss = undefined;
        }

        return (
            <span
                title={ariaLabel || null}
                aria-label={ariaLabel || null}
                role={role}
                aria-hidden={!ariaLabel}
                className={wrapperClassName}
                style={wrapperCss}>
                { this.state.svgComponent ? this.renderSvgComponent(this.state.svgComponent) : (<span />) }
            </span>
        );
    }
}